import type { WorkspaceBlock } from "@/lib/api/workspace-pages";
import type { WorkspaceDocumentJson } from "./WorkspaceTiptapEditor";

export const EMPTY_DOCUMENT: WorkspaceDocumentJson = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

function textContent(text: string) {
  return text ? [{ type: "text", text }] : undefined;
}

function legacyBlockToDocumentNode(block: WorkspaceBlock): WorkspaceDocumentJson {
  const text = block.content.text || "";
  if (block.type === "heading") {
    return { type: "heading", attrs: { level: 1 }, content: textContent(text) };
  }
  if (block.type === "todo") {
    return {
      type: "taskList",
      content: [
        {
          type: "taskItem",
          attrs: { checked: block.content.checked === true },
          content: [{ type: "paragraph", content: textContent(text) }],
        },
      ],
    };
  }
  if (block.type === "product") {
    return {
      type: "productCard",
      attrs: {
        title: text || block.linked_offer_id || "Товар",
        offerId: block.linked_offer_id ?? "",
        storeId: block.linked_store_id ?? "",
        listingKey: block.listing_key ?? "",
      },
    };
  }
  if (block.type === "view") {
    return {
      type: "miniView",
      attrs: {
        title: text || "Мини-вид товаров",
        viewId: block.linked_view_id ?? "",
        filter: "",
        sort: "",
      },
    };
  }
  if (block.type === "task") {
    return { type: "paragraph", content: textContent(text ? `Задача: ${text}` : "Задача") };
  }
  return { type: "paragraph", content: textContent(text) };
}

function isDocumentJson(value: unknown): value is WorkspaceDocumentJson {
  return !!value && typeof value === "object" && !Array.isArray(value) && (value as { type?: unknown }).type === "doc";
}

export function getLatestDocumentBlock(blocks: WorkspaceBlock[]): WorkspaceBlock | null {
  const documentBlocks = blocks.filter(block => block.type === "document");
  if (documentBlocks.length === 0) return null;
  return documentBlocks.sort((a, b) => b.updated_at.localeCompare(a.updated_at) || b.position - a.position)[0] ?? null;
}

export function blocksToDocument(blocks: WorkspaceBlock[]): WorkspaceDocumentJson {
  const documentBlock = getLatestDocumentBlock(blocks);
  if (documentBlock && isDocumentJson(documentBlock.content.doc)) return documentBlock.content.doc;

  const legacyBlocks = blocks.filter(block => block.type !== "document");
  if (legacyBlocks.length === 0) return EMPTY_DOCUMENT;
  return { type: "doc", content: legacyBlocks.map(legacyBlockToDocumentNode) };
}

export function upsertLocalDocumentBlock(blocks: WorkspaceBlock[], documentBlock: WorkspaceBlock): WorkspaceBlock[] {
  const rest = blocks.filter(block => block.id !== documentBlock.id && block.type !== "document");
  return [documentBlock, ...rest].map((block, position) => ({ ...block, position }));
}
